import PropTypes from 'prop-types';
import React, { PureComponent, Fragment } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import { FormattedMessage } from 'react-intl';
import get from 'lodash.get';
import { BarHeader } from 'dibs-elements/exports/BarHeader';
import { ModalCloseButton } from 'dibs-elements/exports/ModalCloseButton';
import { ModalContainer } from 'dibs-elements/exports/ModalContainer';
import LaptopAndPhone from 'dibs-vg/dist/react/laptop-and-phone';
import PhoneRingingInHand from 'dibs-vg/dist/react/phone-ringing-in-hand';
import NumberInputDisplay from 'dibs-vg/dist/react/number-input-display';
import PhoneRinging from 'dibs-vg/dist/react/phone-ringing';

import CallDealerForm from '../CallDealerForm';
import CallInitiatedSuccess from '../CallInitiatedSuccess';
import * as tracking from '../../tracking';
import InitiateCallMutation from '../../mutations/InitiateCall';

import styles from './styles.scss';

const defaultCountryCode = '1';
const numbersOnlyRegex = /^\d+$/;
const containsLettersRegex = /[a-z]/i;

const unknownServiceError = (
    <FormattedMessage
        id="dc.buyerInitiateCall.unknown_service_error"
        defaultMessage="Unknown error occurred. Please try again later."
    />
);

const invalidInput = (
    <FormattedMessage
        id="dc.buyerInitiateCall.invalid_input"
        defaultMessage="Phone number format is invalid. Include a country code and the correct number of digits."
    />
);

const initialState = props => {
    const name = get(props.viewer, 'viewerUser.profile.displayName') || '';

    const existingPhone = !!get(props.viewer, 'primaryPhone.isActive');
    const phoneCallbackEnabled = get(
        props.viewer,
        'conversation.currentConversationMember.phoneCallbackEnabled'
    );
    const savePhoneNumber = !existingPhone;
    let countryCode = defaultCountryCode;
    let phoneNumber = '';

    if (existingPhone) {
        countryCode = get(props.viewer, 'primaryPhone.countryCodeNumber');
        phoneNumber = get(props.viewer, 'primaryPhone.formattedPhoneNumber');
    }

    return {
        name,
        countryCode,
        phoneNumber,
        savePhoneNumber,
        phoneCallbackEnabled: phoneCallbackEnabled !== false,
        submitting: false,
        success: false,
        error: undefined,
    };
};

const resetForm = () => ({ submitting: false });
const resetSuccess = () => ({ success: false });
const submitForm = () => ({ submitting: true, error: undefined });
const submitSuccess = () => ({
    success: true,
    submitting: false,
    savePhoneNumber: false,
});

const updateCountryCode = countryCode => () => ({
    countryCode,
    savePhoneNumber: true,
    error: undefined,
});
const updatePhoneNumber = phoneNumber => () => ({
    phoneNumber,
    savePhoneNumber: true,
    error: undefined,
});
const updateCallbackEnabled = callbackEnabled => () => ({ phoneCallbackEnabled: callbackEnabled });
const setErrorMessage = error => () => ({ error, submitting: false });

const headerSteps = [
    {
        icon: <LaptopAndPhone className={styles.icon} />,
        key: 'LaptopAndPhone',
        copy: (
            <FormattedMessage
                id="dc.buyerInitiateCall.haveYourPhone"
                defaultMessage="Have your phone handy."
            />
        ),
    },
    {
        icon: <NumberInputDisplay className={styles.icon} />,
        key: 'NumberInput',
        copy: (
            <FormattedMessage
                id="dc.buyerInitiateCall.enterNumber"
                defaultMessage="Enter your phone number."
            />
        ),
    },
    {
        icon: <PhoneRingingInHand className={styles.icon} />,
        key: 'PhoneRingingInHand',
        copy: (
            <FormattedMessage
                id="dc.buyerInitiateCall.getCall"
                defaultMessage="You’ll get a call, connecting you with the seller."
            />
        ),
    },
];

export class CallDealerModal extends PureComponent {
    constructor(props) {
        super(props);
        this.state = initialState(props);

        if (!props.userId && !props.onAuthRequired) {
            throw new Error(
                'No userId prop given. Must have a handler for when auth is then required.'
            );
        }
        if (props.isOpened && props.location) {
            tracking.onCallModalOpen(props.location);
        }

        this.initiateCall = this.initiateCall.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.onSuccess = this.onSuccess.bind(this);
        this.onFailure = this.onFailure.bind(this);
        this.onClose = this.onClose.bind(this);
    }

    componentWillReceiveProps(nextProps) {
        const previousPhone = get(this.props.viewer, 'primaryPhone');
        const currentPhone = get(nextProps.viewer, 'primaryPhone');
        const updatePhone = !previousPhone && currentPhone;

        if (!this.state.submitting && updatePhone) {
            this.setState(() => initialState(nextProps));
        }
        if (!this.props.isOpened && nextProps.isOpened && nextProps.location) {
            tracking.onCallModalOpen(nextProps.location);
        }
    }

    initiateCall(buyerId) {
        const { seller, item, relay } = this.props;
        const {
            name,
            phoneNumber,
            countryCode,
            savePhoneNumber,
            phoneCallbackEnabled,
        } = this.state;
        InitiateCallMutation.commit(relay.environment, {
            buyerId,
            saveName: false,
            savePhoneNumber,
            buyerName: name,
            // strip out non-numeric characters
            buyerNumber: phoneNumber.replace(/\D/g, ''),
            buyerCountryCode: countryCode,
            itemId: get(item, 'serviceId'),
            itemName: get(item, 'title'),
            dealerId: get(seller, 'serviceId'),
            phoneCallbackEnabled,
            onSuccess: this.onSuccess,
            onFailure: this.onFailure,
        });
    }

    onSubmit() {
        const { userId, onAuthRequired } = this.props;
        const { phoneNumber, countryCode } = this.state;

        const invalidCountryCode = !numbersOnlyRegex.test(countryCode);
        const invalidPhoneNumber = containsLettersRegex.test(phoneNumber);

        if (invalidCountryCode || invalidPhoneNumber) {
            this.setState(setErrorMessage(invalidInput));
            return;
        }

        this.setState(submitForm);

        if (!userId) {
            onAuthRequired(
                this.initiateCall,
                () => this.setState(resetForm) // if auth fails
            );
            return;
        }

        this.initiateCall(userId);
    }

    onSuccess({ initiateCall }) {
        const { success, errors } = initiateCall;
        const { location } = this.props;
        const { phoneCallbackEnabled } = this.state;

        if (success) {
            this.setState(submitSuccess);
            if (location) {
                tracking.onCallModalSuccess(location, phoneCallbackEnabled);
            }
        } else if (errors && errors.length) {
            this.setState(setErrorMessage(errors[0].message));
        } else {
            this.setState(setErrorMessage(unknownServiceError));
        }
    }

    onFailure() {
        this.setState(setErrorMessage(unknownServiceError));
    }

    onClose() {
        this.setState(resetSuccess);
        this.props.onClose();
    }

    render() {
        const { isOpened, seller } = this.props;
        const dealerLocation = get(seller, 'shippingAddress.displayCityStateCountry');

        let title;
        let header;
        let prompt;
        let subtitle;
        if (this.state.success) {
            title = (
                <FormattedMessage
                    id="dc.buyerInitiateCall.modalSuccessHeader"
                    data-tn="call-initiated"
                    defaultMessage="Expect Our Call"
                />
            );
            header = (
                <Fragment>
                    <div className={styles.wrapper}>
                        <BarHeader title={title} subtitle={subtitle} />
                        <div className={styles.confirmationHeader}>
                            <PhoneRinging className={styles.iconRinging} />
                            <div className={styles.stepDescription}>
                                <FormattedMessage
                                    id="dc.buyerInitiateCall.call_initiated_description"
                                    defaultMessage="When you answer, you’ll hear ringing until the seller picks up."
                                />
                            </div>
                        </div>
                    </div>
                </Fragment>
            );
            prompt = <CallInitiatedSuccess />;
        } else {
            title = (
                <FormattedMessage
                    id="dc.buyerInitiateCall.modalFormHeader"
                    defaultMessage="Set Up a Call with the Seller"
                />
            );
            subtitle = dealerLocation ? (
                <FormattedMessage
                    id="dc.buyerInitiateCall.modalFormSubHeader"
                    defaultMessage="Located in {dealerLocation}"
                    values={{ dealerLocation }}
                />
            ) : null;

            header = (
                <div className={styles.wrapper}>
                    <BarHeader title={title} subtitle={subtitle} />
                    <div className={styles.steps}>
                        {headerSteps.map((step, key) => {
                            return (
                                <div className={styles.step} key={step.key}>
                                    {step.icon}
                                    <div className={styles.stepNumber}>{key + 1}.</div>
                                    <div className={styles.stepDescription}>{step.copy}</div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            );
            prompt = (
                <CallDealerForm
                    showAllowCallbackPrompt
                    phoneNumber={this.state.phoneNumber}
                    countryCode={this.state.countryCode}
                    phoneCallbackEnabled={this.state.phoneCallbackEnabled}
                    submitting={this.state.submitting}
                    error={this.state.error}
                    setPhoneNumber={phoneNumber => this.setState(updatePhoneNumber(phoneNumber))}
                    setCountryCode={countryCode => this.setState(updateCountryCode(countryCode))}
                    setPhoneCallbackEnabled={enabled =>
                        this.setState(updateCallbackEnabled(enabled))
                    }
                    onSubmit={this.onSubmit}
                />
            );
        }

        return (
            <ModalContainer isOpen={isOpened} onClose={this.onClose} modalPosition="center">
                <ModalCloseButton onClick={this.onClose} />
                {header}
                {prompt}
            </ModalContainer>
        );
    }
}

CallDealerModal.propTypes = {
    relay: PropTypes.object,
    userId: PropTypes.string,
    isOpened: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    onAuthRequired: PropTypes.func,
    viewer: PropTypes.object,
    seller: PropTypes.object,
    item: PropTypes.object,
    location: PropTypes.oneOf(['pdp', 'storefront', 'mc', 'mcEmail']),
};

export default createFragmentContainer(CallDealerModal, {
    conversation: graphql`
        fragment CallDealerModal_conversation on Conversation {
            currentConversationMember: currentMember(currentUserId: $userId)
                @include(if: $hasUserId) {
                phoneCallbackEnabled
            }
        }
    `,
    item: graphql`
        fragment CallDealerModal_item on Item {
            serviceId
            title
        }
    `,
    seller: graphql`
        fragment CallDealerModal_seller on Seller {
            serviceId
            shippingAddress {
                displayCityStateCountry
            }
        }
    `,
    viewer: graphql`
        fragment CallDealerModal_viewer on Viewer {
            viewerUser: user(userId: $userId) @include(if: $hasUserId) {
                profile {
                    displayName(preventEmailFallback: true)
                }
            }
            primaryPhone(userId: $userId) @include(if: $hasUserId) {
                countryCodeNumber
                formattedPhoneNumber
                isActive: hasStatus(status: "ACTIVE")
            }
        }
    `,
});
